import React, { useState } from 'react';
import { array, func } from 'prop-types';
import { propTypes } from '../../util/types';
import { HiOutlineTrash } from 'react-icons/hi2';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { MAX_LISTING_IMAGES } from '../../util/listingPackages';
import { AddPortfolioImages, ImageCropModal, ImageDropzone } from '../../components';
import FieldHeader from './FieldHeader';

import css from './EditServiceListingForm.css';

const INPUT_ID = 'addImage';
const CROP_ASPECT = 16 / 9;
// The editor page loads listing images with these variants
const SAVED_IMAGE_VARIANTS = ['landscape-crop', 'landscape-crop2x'];

const noop = () => null;

/**
 * Listing images: thumbnails (the first is the cover), and a drop zone while
 * there are fewer than 3. Picked or dropped files are cropped to 16:9 before
 * onImageUpload. Old listings can have more than 3 images; they must be
 * trimmed, so the field shows an error and the Images check stays undone.
 */
const ServiceImagesFieldComponent = props => {
  const {
    images,
    uploadError,
    onImageUpload,
    onRemoveImage,
    onManageDisableScrolling,
    intl,
  } = props;
  const [crop, setCrop] = useState({ isOpen: false, src: null, fileName: null });
  // The cropped image couldn't be encoded (e.g. too big for the browser)
  const [cropFailed, setCropFailed] = useState(false);

  const handleFileSelected = file => {
    setCropFailed(false);
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setCrop({
        isOpen: true,
        src: reader.result.toString(),
        // The cropped file's name is the temporary image id
        fileName: `${Date.now()}_${file.name}`,
      });
    });
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = croppedFile => {
    setCrop(state => ({ ...state, isOpen: false }));
    onImageUpload({ id: croppedFile.name, file: croppedFile });
  };

  // Close the crop so the error below the field is visible
  const handleCropError = () => {
    setCrop(state => ({ ...state, isOpen: false }));
    setCropFailed(true);
  };

  const dropzone = (
    <ImageDropzone
      id={INPUT_ID}
      className={css.dropzone}
      onFileSelected={handleFileSelected}
      hint={<FormattedMessage id="ImageDropzone.hint" />}
    />
  );

  // The drop zone, or why it is gone, follows the thumbnails
  let afterThumbnails = dropzone;
  if (images.length > MAX_LISTING_IMAGES) {
    afterThumbnails = (
      <p className={css.imagesError} role="alert">
        <FormattedMessage
          id="EditServiceListingForm.tooManyImages"
          values={{ max: MAX_LISTING_IMAGES }}
        />
      </p>
    );
  } else if (images.length === MAX_LISTING_IMAGES) {
    afterThumbnails = (
      <p className={css.imagesLimitNote}>
        <FormattedMessage
          id="EditServiceListingForm.imagesLimitNote"
          values={{ max: MAX_LISTING_IMAGES }}
        />
      </p>
    );
  }

  return (
    <div className={css.field}>
      <FieldHeader
        label={<FormattedMessage id="EditServiceListingForm.imagesLabel" />}
        description={<FormattedMessage id="EditServiceListingForm.imagesDescription" />}
      />
      {/* Block layout: the crop modal's hidden canvas must not add a flex gap */}
      <div>
        {images.length > 0 ? (
          <div className={css.images}>
            <AddPortfolioImages
              className={css.imagesGrid}
              thumbnailClassName={css.thumbnail}
              images={images}
              savedImageAltText={intl.formatMessage({
                id: 'EditServiceListingForm.savedImageAltText',
              })}
              savedImageVariants={SAVED_IMAGE_VARIANTS}
              onRemoveImage={onRemoveImage}
              imageUploadInProgress={noop}
              showCoverBadge
              deleteIcon={<HiOutlineTrash className={css.removeImageIcon} />}
              deleteLabel={intl.formatMessage({ id: 'EditServiceListingForm.removeImage' })}
            >
              {afterThumbnails}
            </AddPortfolioImages>
          </div>
        ) : (
          dropzone
        )}
        <ImageCropModal
          id="EditServiceListingForm.cropImage"
          isOpen={crop.isOpen}
          src={crop.src}
          fileName={crop.fileName || 'image'}
          aspect={CROP_ASPECT}
          aspectChangeable={false}
          onAspectChange={noop}
          instructions={<FormattedMessage id="EditServiceListingForm.cropInstructions" />}
          confirmLabel={<FormattedMessage id="EditServiceListingForm.cropConfirm" />}
          onConfirm={handleCropConfirm}
          onError={handleCropError}
          onClose={() => setCrop(state => ({ ...state, isOpen: false }))}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      </div>
      {/* A failed upload drops its thumbnail, a failed crop never adds one; say why */}
      {uploadError || cropFailed ? (
        <p className={css.imagesError} role="alert">
          <FormattedMessage id="EditServiceListingForm.imageUploadFailed" />
        </p>
      ) : null}
    </div>
  );
};

ServiceImagesFieldComponent.defaultProps = {
  uploadError: null,
};

ServiceImagesFieldComponent.propTypes = {
  // Saved images and uploads ({ id, imageId, file })
  images: array.isRequired,
  uploadError: propTypes.error,
  onImageUpload: func.isRequired,
  onRemoveImage: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  intl: intlShape.isRequired,
};

const ServiceImagesField = injectIntl(ServiceImagesFieldComponent);

export default ServiceImagesField;
